/**
 * Impressão de etiquetas — a tela que transforma SKU em papel colado na peça.
 *
 * É a única tela do catálogo que trabalha ACHATADA: a busca cruza todos os
 * produtos da loja de uma vez (`GET /produtos/skus`), porque quem etiqueta um
 * recebimento tem camiseta, tênis e corda na mesma caixa — obrigar uma ficha
 * por produto seria obrigar uma impressão por produto.
 *
 * ## O que esta tela não faz, e por quê
 *
 * O protótipo tem saída térmica em ZPL, quatro modelos de etiqueta, "puxar do
 * recebimento #1284" e modelos salvos. Nada disso existe aqui, e a razão de
 * cada um está em `docs/de-para.md`. A regra que decidiu os quatro é a mesma:
 * não existe integração com impressora térmica, nem tabela de pedido de
 * compra — e um botão que promete enviar ZPL para um cabo que não existe é
 * pior do que a ausência do botão.
 *
 * ## A seleção sobrevive à busca
 *
 * Marcar, buscar outra coisa e marcar de novo acumula. É o motivo de a
 * seleção ser um mapa de SKU, e não uma lista de índices da tabela visível:
 * numa tela cuja razão de existir é juntar peças de produtos diferentes, uma
 * seleção que se perde a cada digitação não junta nada.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal, flushSync } from 'react-dom'

import { Botao } from '../../componentes/Botao'
import { ID_AREA_DE_IMPRESSAO } from '../../componentes/BotaoExportar'
import { Caixa } from '../../componentes/Campos'
import { Icone } from '../../componentes/Icone'
import { Segmentado } from '../../componentes/Segmentado'
import { ErroApi } from '../../plataforma/api'
import { useSessao } from '../../plataforma/contexto-sessao'
import { formatarMoeda } from '../../plataforma/dinheiro'
import { carregarSKUs, CATEGORIAS, type Categoria, type LinhaSKU } from '../../plataforma/produtos'
import { LayoutApp } from '../app/LayoutApp'
import { CartaoDaEtiqueta, FolhaDeEtiquetas } from './EtiquetaImpressa'
import {
  CAMPOS_DA_ETIQUETA,
  CONTEUDO_PADRAO,
  conteudoVazio,
  etiquetasPorFolha,
  expandirEtiquetas,
  FOLHA_PADRAO,
  FOLHAS,
  folhaPorChave,
  folhasNecessarias,
  MODOS_DE_QUANTIDADE,
  medidaDaEtiqueta,
  paginarEtiquetas,
  quantidadeDigitada,
  quantidadePadrao,
  totalDeEtiquetas,
  type Conteudo,
  type ItemSelecionado,
  type ModoDeQuantidade,
} from './etiquetas'

type FiltroCategoria = Categoria | 'todas'

const FILTROS_CATEGORIA: { chave: FiltroCategoria; rotulo: string }[] = [
  { chave: 'todas', rotulo: 'Todas' },
  ...CATEGORIAS.map((c) => ({ chave: c.chave as FiltroCategoria, rotulo: c.rotulo })),
]

export function TelaEtiquetas() {
  const { situacao } = useSessao()
  const loja = situacao.estado === 'autenticado' ? situacao.sessao.loja : null
  const nomeDaLoja = loja ? loja.nome_fantasia || loja.nome : ''

  const [busca, setBusca] = useState('')
  const [buscaAtiva, setBuscaAtiva] = useState('')
  const [categoria, setCategoria] = useState<FiltroCategoria>('todas')

  const [skus, setSkus] = useState<LinhaSKU[] | null>(null)
  const [erro, setErro] = useState('')

  const [selecao, setSelecao] = useState<Record<string, LinhaSKU>>({})
  const [modo, setModo] = useState<ModoDeQuantidade>('estoque')
  const [quantidades, setQuantidades] = useState<Record<string, number>>({})

  const [folhaChave, setFolhaChave] = useState(FOLHA_PADRAO.chave)
  const [comecarEmTexto, setComecarEmTexto] = useState('1')
  const [comBorda, setComBorda] = useState(true)
  const [conteudo, setConteudo] = useState<Conteudo>(CONTEUDO_PADRAO)

  const [imprimindo, setImprimindo] = useState(false)

  // Mesmo debounce do estoque: cada tecla disparando um JOIN em três tabelas é
  // como se derruba o próprio banco em dia de movimento.
  useEffect(() => {
    const relogio = setTimeout(() => setBuscaAtiva(busca), 300)
    return () => clearTimeout(relogio)
  }, [busca])

  useEffect(() => {
    const controle = new AbortController()

    carregarSKUs(
      { busca: buscaAtiva, categoria: categoria === 'todas' ? '' : categoria },
      controle.signal,
    )
      .then((dados) => {
        setSkus(dados.skus)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar os SKUs.')
      })

    return () => controle.abort()
  }, [buscaAtiva, categoria])

  // A view de impressão só existe enquanto dura a impressão — a mesma razão de
  // `BotaoExportar`: mil etiquetas montadas no DOM em toda visita à tela.
  useEffect(() => {
    if (!imprimindo) return
    const aoTerminar = () => setImprimindo(false)
    window.addEventListener('afterprint', aoTerminar)
    return () => window.removeEventListener('afterprint', aoTerminar)
  }, [imprimindo])

  const folha = folhaPorChave(folhaChave)
  const porFolha = etiquetasPorFolha(folha)
  const comecarEm = Math.min(Math.max(1, Number(comecarEmTexto.replace(/\D/g, '')) || 1), porFolha)

  const quantidadeDe = useCallback(
    (sku: LinhaSKU) => quantidades[sku.codigo_barras] ?? quantidadePadrao(modo, sku.saldo),
    [quantidades, modo],
  )

  const itens: ItemSelecionado[] = useMemo(
    () =>
      Object.values(selecao)
        .sort((a, b) =>
          a.produto.localeCompare(b.produto, 'pt-BR') ||
          a.cor.localeCompare(b.cor, 'pt-BR') ||
          a.tamanho.localeCompare(b.tamanho, 'pt-BR', { numeric: true }),
        )
        .map((sku) => ({ sku, quantidade: quantidadeDe(sku) })),
    [selecao, quantidadeDe],
  )

  const total = totalDeEtiquetas(itens)
  const folhas = folhasNecessarias(total, comecarEm, porFolha)
  const semConteudo = conteudoVazio(conteudo)
  const visiveis = skus ?? []
  const marcados = Object.keys(selecao).length
  // Marcado numa busca anterior e fora do filtro de agora. Sem dizer isso, o
  // resumo parece contar errado.
  const foraDoFiltro = marcados - visiveis.filter((s) => selecao[s.codigo_barras]).length

  const alternar = (sku: LinhaSKU, marcar: boolean) => {
    setSelecao((atual) => {
      const proximo = { ...atual }
      if (marcar) proximo[sku.codigo_barras] = sku
      else delete proximo[sku.codigo_barras]
      return proximo
    })
  }

  const todosVisiveisMarcados =
    visiveis.length > 0 && visiveis.every((s) => selecao[s.codigo_barras])

  const alternarTodos = () => {
    setSelecao((atual) => {
      const proximo = { ...atual }
      for (const sku of visiveis) {
        if (todosVisiveisMarcados) delete proximo[sku.codigo_barras]
        else proximo[sku.codigo_barras] = sku
      }
      return proximo
    })
  }

  const imprimir = () => {
    // `flushSync` monta a folha AGORA: `window.print()` é síncrono e não espera
    // o próximo commit do React. Sem isto, o diálogo abriria com o estado
    // anterior a este clique — o mesmo motivo do `BotaoExportar`.
    flushSync(() => setImprimindo(true))
    window.print()
  }

  const primeiro = itens[0]?.sku

  return (
    <LayoutApp
      titulo="Etiquetas"
      subtitulo={
        skus === null
          ? 'Carregando…'
          : `${skus.length} ${skus.length === 1 ? 'SKU disponível' : 'SKUs disponíveis'} · ${marcados} ${marcados === 1 ? 'selecionado' : 'selecionados'}`
      }
      trilha={[{ rotulo: 'Produtos', para: '/app/produtos' }, { rotulo: 'Etiquetas' }]}
      voltarPara="/app/produtos"
    >
      <div className="grid w-full grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex min-w-0 flex-col gap-4">
          {erro && (
            <div
              role="alert"
              className="rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto"
            >
              {erro}
            </div>
          )}

          <CartaoFolha
            folhaChave={folhaChave}
            aoTrocarFolha={setFolhaChave}
            comecarEmTexto={comecarEmTexto}
            aoTrocarComecarEm={setComecarEmTexto}
            porFolha={porFolha}
            comBorda={comBorda}
            aoTrocarBorda={setComBorda}
          />

          <CartaoConteudo
            conteudo={conteudo}
            aoTrocar={setConteudo}
            semConteudo={semConteudo}
          />

          <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
            <div className="flex flex-wrap items-center gap-3 border-b border-borda px-4 py-3.5">
              <div className="min-w-0 flex-1 basis-[200px]">
                <h2 className="font-titulo text-base font-semibold text-titulo">O que imprimir</h2>
                <p className="mt-0.5 text-[12.5px] text-texto-suave">
                  A seleção acumula entre buscas — marque de um produto, procure outro e marque
                  também.
                </p>
              </div>
              {marcados > 0 && (
                <Botao variante="secundario" onClick={() => setSelecao({})}>
                  Limpar seleção
                </Botao>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2.5 border-b border-borda bg-superficie-suave px-4 py-2.5">
              <label className="composto h-[34px] min-w-0 flex-1 basis-[220px] cursor-text">
                <Icone nome="search" tamanho={18} className="text-texto-fraco" />
                <input
                  value={busca}
                  onChange={(evento) => setBusca(evento.target.value)}
                  placeholder="Buscar produto, código ou código de barras"
                />
                <span className="sr-only">Buscar SKU para etiquetar</span>
              </label>

              <Segmentado
                opcoes={FILTROS_CATEGORIA}
                valor={categoria}
                aoTrocar={setCategoria}
                compacto
              />
            </div>

            <div className="flex flex-wrap items-center gap-2.5 border-b border-borda px-4 py-2.5">
              <span className="text-[12.5px] text-texto-suave">Quantidade padrão</span>
              <Segmentado
                opcoes={MODOS_DE_QUANTIDADE}
                valor={modo}
                aoTrocar={(chave) => {
                  // Trocar o modo descarta o que foi digitado à mão: o modo É a
                  // regra, e manter overrides antigos faria a tabela mostrar
                  // números que nenhuma das duas coisas explica.
                  setModo(chave)
                  setQuantidades({})
                }}
                compacto
              />
            </div>

            <TabelaDeSKUs
              skus={skus}
              selecao={selecao}
              busca={buscaAtiva}
              quantidadeDe={quantidadeDe}
              aoAlternar={alternar}
              aoAlternarTodos={alternarTodos}
              todosMarcados={todosVisiveisMarcados}
              aoDigitarQuantidade={(sku, texto) =>
                setQuantidades((atual) => ({
                  ...atual,
                  [sku.codigo_barras]: quantidadeDigitada(texto),
                }))
              }
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 xl:sticky xl:top-4">
          <div className="rounded-sistema border border-borda bg-superficie p-5">
            <div className="mb-3.5 flex items-center justify-between gap-3">
              <h2 className="font-titulo text-[15px] font-semibold text-titulo">Prévia</h2>
              <span className="tabular text-xs text-texto-suave">
                {medidaDaEtiqueta(folha).largura} × {medidaDaEtiqueta(folha).altura} mm
              </span>
            </div>

            {primeiro && !semConteudo ? (
              <div className="flex justify-center overflow-x-auto rounded-sistema border border-dashed border-borda bg-superficie-suave p-4">
                <CartaoDaEtiqueta
                  sku={primeiro}
                  conteudo={conteudo}
                  nomeDaLoja={nomeDaLoja}
                  folha={folha}
                  comBorda
                />
              </div>
            ) : (
              <p className="rounded-sistema border border-dashed border-borda bg-superficie-suave px-4 py-8 text-center text-[13px] leading-[1.55] text-texto-suave">
                {semConteudo
                  ? 'Ligue ao menos um campo acima — uma etiqueta sem nada impresso é um adesivo em branco.'
                  : 'Selecione um SKU abaixo para ver como a etiqueta dele fica.'}
              </p>
            )}

            <MapaDaFolha
              colunas={folha.colunas}
              linhas={folha.linhas}
              comecarEm={comecarEm}
              total={total}
            />
          </div>

          {/* `section` com nome: "Etiquetas" e "Folhas A4" são palavras que
              também aparecem na tabela e no menu, e o número que vale é o
              deste cartão. */}
          <section
            aria-label="Resumo da impressão"
            className="rounded-sistema border border-borda bg-superficie p-5"
          >
            <h2 className="font-titulo mb-3 text-[15px] font-semibold text-titulo">Resumo</h2>

            <LinhaResumo rotulo="SKUs selecionados" valor={String(marcados)} />
            {foraDoFiltro > 0 && (
              <LinhaResumo
                rotulo="Fora do filtro atual"
                valor={String(foraDoFiltro)}
                discreta
              />
            )}
            <LinhaResumo rotulo="Etiquetas" valor={String(total)} forte />
            <LinhaResumo rotulo="Folhas A4" valor={String(folhas)} />

            <div className="mt-4">
              <Botao
                onClick={imprimir}
                disabled={total === 0 || semConteudo}
                porte="cta"
                icone="imprimir"
                larguraTotal
              >
                {total === 0
                  ? 'Nada selecionado'
                  : `Imprimir ${total} ${total === 1 ? 'etiqueta' : 'etiquetas'}`}
              </Botao>
            </div>

            <p className="mt-3 text-[12.5px] leading-[1.55] text-texto-suave">
              A folha abre no diálogo de impressão do navegador — de lá dá para salvar em PDF ou
              mandar para qualquer impressora comum. Impressora térmica ainda não é suportada.
            </p>
          </section>
        </div>
      </div>

      {imprimindo &&
        createPortal(
          <div id={ID_AREA_DE_IMPRESSAO}>
            <FolhaDeEtiquetas
              paginas={paginarEtiquetas(expandirEtiquetas(itens), comecarEm, porFolha)}
              folha={folha}
              conteudo={conteudo}
              nomeDaLoja={nomeDaLoja}
              comBorda={comBorda}
            />
          </div>,
          document.body,
        )}
    </LayoutApp>
  )
}

/* ------------------------------------------------------------------ folha */

function CartaoFolha({
  folhaChave,
  aoTrocarFolha,
  comecarEmTexto,
  aoTrocarComecarEm,
  porFolha,
  comBorda,
  aoTrocarBorda,
}: {
  folhaChave: string
  aoTrocarFolha: (chave: string) => void
  comecarEmTexto: string
  aoTrocarComecarEm: (texto: string) => void
  porFolha: number
  comBorda: boolean
  aoTrocarBorda: (valor: boolean) => void
}) {
  return (
    <div className="rounded-sistema border border-borda bg-superficie px-[18px] py-4">
      <h2 className="font-titulo text-base font-semibold text-titulo">Folha</h2>
      <p className="mt-0.5 mb-4 text-[12.5px] text-texto-suave">
        A grade divide a A4 em partes iguais, com 10 mm de margem. Não é a geometria de um papel
        adesivo de catálogo — confira numa folha comum antes de gastar adesivo.
      </p>

      <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <label
            htmlFor="etiquetas-folha"
            className="mb-1.5 block text-[12.5px] font-semibold text-tinta"
          >
            Grade da folha
          </label>
          <select
            id="etiquetas-folha"
            className="campo w-full"
            value={folhaChave}
            onChange={(evento) => aoTrocarFolha(evento.target.value)}
          >
            {FOLHAS.map((f) => (
              <option key={f.chave} value={f.chave}>
                {f.rotulo}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="etiquetas-comecar"
            className="mb-1.5 block text-[12.5px] font-semibold text-tinta"
          >
            Começar na posição
          </label>
          <input
            id="etiquetas-comecar"
            inputMode="numeric"
            className="campo tabular w-full"
            value={comecarEmTexto}
            onChange={(evento) => aoTrocarComecarEm(evento.target.value.replace(/\D/g, ''))}
          />
          <p className="mt-1.5 text-[11.5px] text-texto-fraco">
            Aproveita folha já usada · 1 a {porFolha}
          </p>
        </div>

        <div className="flex items-end pb-1">
          <Caixa marcada={comBorda} aoMudar={aoTrocarBorda}>
            Imprimir o fio de recorte
          </Caixa>
        </div>
      </div>
    </div>
  )
}

function CartaoConteudo({
  conteudo,
  aoTrocar,
  semConteudo,
}: {
  conteudo: Conteudo
  aoTrocar: (proximo: Conteudo) => void
  semConteudo: boolean
}) {
  return (
    <div className="rounded-sistema border border-borda bg-superficie px-[18px] py-4">
      <h2 className="font-titulo text-base font-semibold text-titulo">Conteúdo da etiqueta</h2>
      <p className="mt-0.5 mb-4 text-[12.5px] text-texto-suave">
        Vale para todas as etiquetas desta impressão.
      </p>

      <div className="grid gap-x-6 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
        {CAMPOS_DA_ETIQUETA.map((campo) => (
          <Caixa
            key={campo.chave}
            marcada={conteudo[campo.chave]}
            aoMudar={(valor) => aoTrocar({ ...conteudo, [campo.chave]: valor })}
          >
            {campo.rotulo}
          </Caixa>
        ))}
      </div>

      {semConteudo && (
        <p role="alert" className="mt-3 text-[12.5px] text-erro-texto">
          Sem nenhum campo ligado a etiqueta sai em branco.
        </p>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------- tabela */

function TabelaDeSKUs({
  skus,
  selecao,
  busca,
  quantidadeDe,
  aoAlternar,
  aoAlternarTodos,
  todosMarcados,
  aoDigitarQuantidade,
}: {
  skus: LinhaSKU[] | null
  selecao: Record<string, LinhaSKU>
  busca: string
  quantidadeDe: (sku: LinhaSKU) => number
  aoAlternar: (sku: LinhaSKU, marcar: boolean) => void
  aoAlternarTodos: () => void
  todosMarcados: boolean
  aoDigitarQuantidade: (sku: LinhaSKU, texto: string) => void
}) {
  if (skus === null) {
    return <div className="m-4 h-[220px] animate-pulse rounded-sistema bg-superficie-suave" />
  }

  if (skus.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2.5 px-4 py-12 text-center">
        <Icone nome="sell" tamanho={26} className="text-inativo" />
        <p className="max-w-[360px] text-[13px] leading-[1.55] text-texto-suave">
          {busca
            ? `Nenhum SKU encontrado para "${busca}".`
            : 'Nenhum SKU ativo no catálogo. Cadastre um produto e a grade de cor × tamanho aparece aqui para etiquetar.'}
        </p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse">
        <caption className="sr-only">SKUs do catálogo disponíveis para etiquetar</caption>
        <thead>
          <tr className="bg-superficie-suave">
            <th scope="col" className="w-11 py-2.5 pl-4">
              <Caixa marcada={todosMarcados} aoMudar={aoAlternarTodos}>
                <span className="sr-only">Selecionar todos os SKUs da lista</span>
              </Caixa>
            </th>
            <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-texto-suave">
              SKU
            </th>
            <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-texto-suave">
              Variação
            </th>
            <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-texto-suave">
              Código de barras
            </th>
            <th scope="col" className="px-3 py-2.5 text-right text-xs font-semibold text-texto-suave">
              Preço
            </th>
            <th scope="col" className="px-3 py-2.5 text-right text-xs font-semibold text-texto-suave">
              Estoque
            </th>
            <th scope="col" className="py-2.5 pr-4 pl-3 text-right text-xs font-semibold text-texto-suave">
              Etiquetas
            </th>
          </tr>
        </thead>
        <tbody>
          {skus.map((sku) => {
            const marcado = Boolean(selecao[sku.codigo_barras])
            return (
              <tr
                key={sku.codigo_barras}
                className={`border-t border-divisoria ${marcado ? 'bg-marca-suave' : ''}`}
              >
                <td className="py-2.5 pl-4">
                  <Caixa marcada={marcado} aoMudar={(valor) => aoAlternar(sku, valor)}>
                    <span className="sr-only">
                      Selecionar {sku.produto} {sku.cor} {sku.tamanho}
                    </span>
                  </Caixa>
                </td>
                <td className="px-3 py-2.5">
                  <div className="tabular text-[13px] font-semibold text-tinta">
                    {sku.codigo_legivel}
                  </div>
                  <div className="text-xs text-texto-suave">{sku.produto}</div>
                </td>
                <td className="px-3 py-2.5 text-[13px] text-texto">
                  {sku.cor} · {sku.tamanho}
                </td>
                <td className="tabular px-3 py-2.5 text-[12.5px] text-texto-suave">
                  {sku.codigo_barras}
                </td>
                <td className="tabular px-3 py-2.5 text-right text-[13px] font-semibold text-tinta">
                  {formatarMoeda(sku.preco_centavos)}
                </td>
                <td
                  className={`tabular px-3 py-2.5 text-right text-[13px] ${
                    sku.saldo <= 0 ? 'text-erro-texto' : 'text-texto-suave'
                  }`}
                >
                  {sku.saldo}
                </td>
                <td className="py-2.5 pr-4 pl-3 text-right">
                  <input
                    inputMode="numeric"
                    aria-label={`Etiquetas de ${sku.produto} ${sku.cor} ${sku.tamanho}`}
                    className="campo tabular h-[34px] w-[68px] text-center"
                    value={quantidadeDe(sku)}
                    onChange={(evento) => aoDigitarQuantidade(sku, evento.target.value)}
                  />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/* ---------------------------------------------------------------- resumo */

function LinhaResumo({
  rotulo,
  valor,
  forte = false,
  discreta = false,
}: {
  rotulo: string
  valor: string
  forte?: boolean
  discreta?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-divisoria py-2.5 text-[13px]">
      <span className={discreta ? 'text-texto-fraco' : 'text-texto-suave'}>{rotulo}</span>
      <span className={`tabular ${forte ? 'font-bold text-tinta' : 'font-semibold text-texto'}`}>
        {valor}
      </span>
    </div>
  )
}

/**
 * O mapa da folha — cinza é posição já usada, marca é o que vai sair.
 *
 * Desenha só a PRIMEIRA folha: é nela que a decisão de "começar na posição N"
 * acontece, e as seguintes saem cheias desde o início.
 */
function MapaDaFolha({
  colunas,
  linhas,
  comecarEm,
  total,
}: {
  colunas: number
  linhas: number
  comecarEm: number
  total: number
}) {
  const porFolha = colunas * linhas
  const celulas = Array.from({ length: porFolha }, (_, i) => {
    const posicao = i + 1
    if (posicao < comecarEm) return 'usada'
    return posicao < comecarEm + total ? 'cheia' : 'livre'
  })

  return (
    <div className="mt-3.5">
      <div className="mb-2 flex justify-between text-xs text-texto-suave">
        <span>Primeira folha</span>
        <span className="tabular">{porFolha} posições</span>
      </div>
      <div
        aria-hidden
        className="rounded-sistema border border-borda bg-superficie p-2.5"
        style={{ display: 'grid', gridTemplateColumns: `repeat(${colunas}, 1fr)`, gap: '3px' }}
      >
        {celulas.map((estado, i) => (
          <div
            key={i}
            style={{ aspectRatio: '2 / 1' }}
            className={`rounded-[2px] border ${
              estado === 'usada'
                ? 'border-divisoria bg-divisoria'
                : estado === 'cheia'
                  ? 'border-marca bg-marca'
                  : 'border-borda bg-superficie'
            }`}
          />
        ))}
      </div>
      <div className="mt-2 flex gap-3.5 text-[11.5px] text-texto-suave">
        <span className="flex items-center gap-1.5">
          <i className="block h-2.5 w-2.5 rounded-[2px] bg-divisoria" aria-hidden />
          já usada
        </span>
        <span className="flex items-center gap-1.5">
          <i className="block h-2.5 w-2.5 rounded-[2px] bg-marca" aria-hidden />
          será impressa
        </span>
      </div>
    </div>
  )
}
